import { createSignal, For, Show, createEffect, createMemo, onCleanup } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import wm from '../lib/wmStore.js';
import toast from '../lib/toastStore.js';
import { renderFissalMarkdown } from '../lib/renderMarkdown.jsx';
import { AdminMemoriesPanel } from './AdminMemories.jsx';
import GroundingStudio from './GroundingStudio.jsx';
import { maskModelName } from '../lib/format.js';

// Sub-cent costs render in cents so magnitude is legible at a glance (#272)
function formatCost(cost) {
  const n = Number(cost) || 0;
  if (n === 0) return '$0';
  if (n < 0.01) return `${(n * 100).toFixed(3)}\u00A2`;
  if (n < 1)    return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

function fmtNum(n) {
  return (Number(n) || 0).toLocaleString();
}

function dayLabel(dayNum) {
  const d = new Date(dayNum * 86400000);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function relTime(ts) {
  if (!ts) return '—';
  const ms = Number(ts);
  const real = ms < 1e12 ? ms * 1000 : ms;
  const diff = Date.now() - real;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return new Date(real).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function FissalCost(props) {
  const isAdmin = () => !!wm.state.identity?.isAdmin;

  // ── Navigation tabs: 4 unified pillars ──
  // 'bastion' (Echo & Fissal Co-Pilot) | 'metrics' (Telemetry & Spend) | 'memory' (Neural Memory & Lore) | 'tools' (Live Tools & Test Lab)
  const [tab, setTab] = createSignal(props.settings?.tab || (isAdmin() ? 'bastion' : 'metrics'));
  const [memorySubTab, setMemorySubTab] = createSignal(props.settings?.memorySubTab || 'users'); // 'users' | 'grounding'
  const [toolsSubTab, setToolsSubTab] = createSignal(props.settings?.toolsSubTab || 'sandbox'); // 'sandbox' | 'registry'

  createEffect(() => {
    const wid = props.widget?.id;
    const t = tab();
    const mst = memorySubTab();
    const tst = toolsSubTab();
    if (!wid) return;
    const cur = props.widget?.settings || {};
    if (cur.tab !== t || cur.memorySubTab !== mst || cur.toolsSubTab !== tst) {
      wm.setSettings(wid, { ...cur, tab: t, memorySubTab: mst, toolsSubTab: tst });
    }
  });

  const [limit] = createSignal(100);

  // ── Bastion Engine / Master Switch State ──
  const [msEnabled, setMsEnabled] = createSignal(true);
  const [msModel, setMsModel] = createSignal('gemini-4-argon');
  const [msReasoning, setMsReasoning] = createSignal('AUTO');
  const [msSyncOwu, setMsSyncOwu] = createSignal(true);
  const [msSaving, setMsSaving] = createSignal(false);
  const [msSaved, setMsSaved] = createSignal(false);
  const [msErr, setMsErr] = createSignal('');
  const [routingFlow, setRoutingFlow] = createSignal([]);

  // Available models & providers from backend
  const [availModels, setAvailModels] = createSignal([]);
  const [modelCatalog, setModelCatalog] = createSignal({});
  const [providerStatuses, setProviderStatuses] = createSignal({});
  const [mcLoading, setMcLoading] = createSignal(false);
  const [mcErr, setMcErr] = createSignal('');

  // ── Route Probe / Live Verification State ──
  const [verifyPrompt, setVerifyPrompt] = createSignal('State your name, persona, and calculate 17 * 19.');
  const [verifyLoading, setVerifyLoading] = createSignal(false);
  const [verifyResult, setVerifyResult] = createSignal(null);
  const [verifyErr, setVerifyErr] = createSignal('');

  // ── OpenWebUI Drawer State ──
  const [owuOpen, setOwuOpen] = createSignal(false);
  const [owuLoading, setOwuLoading] = createSignal(false);
  const [owuSaving, setOwuSaving] = createSignal(false);
  const [owuModel, setOwuModel] = createSignal(null);
  const [owuSystem, setOwuSystem] = createSignal('');
  const [owuTemp, setOwuTemp] = createSignal(0.7);
  const [owuTopP, setOwuTopP] = createSignal(0.95);
  const [owuBaseModel, setOwuBaseModel] = createSignal('');

  // ── Tools & Capability Studio State ──
  const [toolsList, setToolsList] = createSignal([]);
  const [toolsLoading, setToolsLoading] = createSignal(false);
  const [toolsCategory, setToolsCategory] = createSignal('All');
  const [toolsSearch, setToolsSearch] = createSignal('');
  const [selectedToolForExec, setSelectedToolForExec] = createSignal(null);
  const [toolExecArgs, setToolExecArgs] = createSignal('{}');
  const [toolExecLoading, setToolExecLoading] = createSignal(false);
  const [toolExecResult, setToolExecResult] = createSignal(null);
  const [toolExecErr, setToolExecErr] = createSignal('');

  // AI Tool Selection Probe
  const [toolProbeLoading, setToolProbeLoading] = createSignal(false);
  const [toolProbeResult, setToolProbeResult] = createSignal(null);
  const [toolProbeErr, setToolProbeErr] = createSignal('');
  const [toolProbePrompt, setToolProbePrompt] = createSignal('Check the health status of all homelab services and report their status.');

  // ── Test Lab (Response Sandbox) State ──
  const [testMode, setTestMode] = createSignal('chat'); // 'chat' | 'discord-preview' | 'rag-probe' | 'suggest-price' | 'bulk-pricing'
  const [testPrompt, setTestPrompt] = createSignal('');
  const [testItemName, setTestItemName] = createSignal('Dreugh Wax');
  const [testBulkItems, setTestBulkItems] = createSignal('Dreugh Wax\nChromium Plating\nTempering Alloy');
  const [testRagProbeKind, setTestRagProbeKind] = createSignal('scout');
  const [testLoading, setTestLoading] = createSignal(false);
  const [testError, setTestError] = createSignal('');
  const [testResult, setTestResult] = createSignal(null);

  // ── Metrics Invocations Table State ──
  const [sortKey, setSortKey] = createSignal('timestamp');
  const [sortDir, setSortDir] = createSignal(-1);
  const [deleteLogsLoading, setDeleteLogsLoading] = createSignal(false);

  // ── Telemetry Data Fetcher ──
  const { data, loading, error, refetch } = useWidgetData('fissal-cost', () => ({
    limit: limit(),
  }));

  const totals = () => data()?.totals || { count: 0, cost: 0, tokensIn: 0, tokensOut: 0 };
  const byModel = () => data()?.byModel || [];
  const daily = () => data()?.dailyCosts || data()?.daily || [];
  const rawInvocations = () => data()?.invocations || [];

  // ── Backend Configuration Loaders ──
  async function loadModelConfig() {
    setMcLoading(true);
    setMcErr('');
    try {
      const r = await fetch('/api/admin/fissal/model-config', { credentials: 'same-origin' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setAvailModels(j.available_models || ['gemini-4-argon', 'gemini-3.8-flash', 'gemini-3.7-flash', 'gemini-2.5-pro']);
      setModelCatalog(j.model_catalog || {});
      setProviderStatuses(j.providers || {});
      if (j.master_switch) {
        setMsEnabled(j.master_switch.enabled !== false);
        if (j.master_switch.modelName) setMsModel(j.master_switch.modelName);
        if (j.master_switch.reasoningLevel) setMsReasoning(j.master_switch.reasoningLevel);
        setMsSyncOwu(j.master_switch.syncOpenWebUi !== false);
      }
      if (Array.isArray(j.tools)) {
        setToolsList(j.tools);
      }
      if (Array.isArray(j.routing_flow)) {
        setRoutingFlow(j.routing_flow);
      }
    } catch (err) {
      setMcErr(err?.message || 'Failed to load model config');
    } finally {
      setMcLoading(false);
    }
  }

  async function loadOpenWebUiModel() {
    setOwuLoading(true);
    try {
      const r = await fetch('/api/admin/fissal/openwebui-model', { credentials: 'same-origin' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (data.model) {
        setOwuModel(data.model);
        setOwuSystem(data.model.params?.system || '');
        setOwuTemp(data.model.params?.temperature ?? 0.7);
        setOwuTopP(data.model.params?.top_p ?? 0.95);
        setOwuBaseModel(data.model.base_model_id || '');
      }
    } catch (err) {
      toast.error(`Failed to load OpenWebUI model: ${err.message}`);
    } finally {
      setOwuLoading(false);
    }
  }

  async function saveOpenWebUiModel() {
    setOwuSaving(true);
    try {
      const r = await fetch('/api/admin/fissal/openwebui-model', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: owuSystem(),
          temperature: Number(owuTemp()),
          top_p: Number(owuTopP()),
          base_model_id: owuBaseModel() || undefined,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('OpenWebUI Fissal model parameters updated!');
      await loadOpenWebUiModel();
    } catch (err) {
      toast.error(`Failed to update OpenWebUI model: ${err.message}`);
    } finally {
      setOwuSaving(false);
    }
  }

  async function loadToolsList() {
    setToolsLoading(true);
    try {
      const r = await fetch('/api/admin/fissal/tools', { credentials: 'same-origin' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (Array.isArray(data.tools)) {
        setToolsList(data.tools);
      }
    } catch (err) {
      toast.error(`Failed to load tools: ${err.message}`);
    } finally {
      setToolsLoading(false);
    }
  }

  async function toggleToolState(toolName, currentEnabled) {
    const nextEnabled = !currentEnabled;
    try {
      const r = await fetch('/api/admin/fissal/tools', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool: toolName, enabled: nextEnabled }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setToolsList(prev => prev.map(t => t.name === toolName ? { ...t, enabled: nextEnabled } : t));
      toast.success(`${toolName} is now ${nextEnabled ? 'ENABLED' : 'DISABLED'}`);
    } catch (err) {
      toast.error(`Tool toggle error: ${err.message}`);
    }
  }

  async function runDirectToolExec() {
    const tool = selectedToolForExec();
    if (!tool) return;
    setToolExecLoading(true);
    setToolExecErr('');
    setToolExecResult(null);
    try {
      let args = {};
      try {
        args = JSON.parse(toolExecArgs() || '{}');
      } catch (pErr) {
        throw new Error(`Invalid JSON parameters: ${pErr.message}`);
      }
      const r = await fetch('/api/admin/fissal/tools/execute', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool: tool.name, args }),
      });
      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setToolExecResult(data);
      toast.success(`Executed ${tool.name} in ${data.duration_ms}ms`);
    } catch (err) {
      setToolExecErr(err.message);
      toast.error(`Tool execution error: ${err.message}`);
    } finally {
      setToolExecLoading(false);
    }
  }

  async function runAiToolProbe() {
    setToolProbeLoading(true);
    setToolProbeErr('');
    setToolProbeResult(null);
    try {
      const r = await fetch('/api/admin/fissal/tools/probe', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: toolProbePrompt() }),
      });
      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setToolProbeResult(data);
      if (data.tool_called) {
        toast.success(`${data.model || 'Gemini 4 Argon'} selected & called: ${data.tool_name}!`);
      } else {
        toast.info('Model formulated a direct answer without tool call.');
      }
    } catch (err) {
      setToolProbeErr(err.message);
      toast.error(`Tool probe failed: ${err.message}`);
    } finally {
      setToolProbeLoading(false);
    }
  }

  // ── Unified Bastion Engine Synchronizer ──
  async function saveUnifiedEngine() {
    setMsSaving(true);
    setMsSaved(false);
    setMsErr('');
    try {
      // 1. Update Master Switch
      const res = await fetch('/api/admin/fissal/master-switch', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: msEnabled(),
          model_name: msModel(),
          reasoning_level: msReasoning(),
          sync_openwebui: msSyncOwu(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);

      // 2. Align core task records (preserve lightweight Flash on rag/memory/intent unless Enforce Master Brain is checked)
      const taskPayload = {};
      const primaryTasks = ['fissal', 'discord', 'pricing', 'relay-assistant', 'code-assist', 'raffle-commentary'];
      const microTasks = ['rag', 'memory', 'intent'];
      for (const t of primaryTasks) {
        taskPayload[t] = {
          model_name: msModel(),
          reasoning_level: msReasoning(),
        };
      }
      if (msEnabled()) {
        for (const t of microTasks) {
          taskPayload[t] = {
            model_name: msModel(),
            reasoning_level: msReasoning(),
          };
        }
      }
      await fetch('/api/admin/fissal/model-config', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskPayload),
      });

      setMsEnabled(data.master_switch.enabled);
      setMsModel(data.master_switch.modelName);
      setMsReasoning(data.master_switch.reasoningLevel);
      setMsSyncOwu(data.master_switch.syncOpenWebUi);
      if (Array.isArray(data.routing_flow)) setRoutingFlow(data.routing_flow);

      setMsSaved(true);
      setTimeout(() => setMsSaved(false), 3500);
      toast.success(`Bastion Engine synchronized with ${msModel()} (${msReasoning()}) across all surfaces!`);
    } catch (err) {
      setMsErr(err.message);
      toast.error(`Engine sync error: ${err.message}`);
    } finally {
      setMsSaving(false);
    }
  }

  // ── Live Route Verification Probe ──
  async function runVerifyRoute() {
    setVerifyLoading(true);
    setVerifyResult(null);
    setVerifyErr('');
    try {
      const res = await fetch('/api/admin/fissal/verify-route', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: verifyPrompt() || 'State your name, persona, and calculate 17 * 19.',
          task: 'fissal',
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setVerifyResult(data);
      toast.success('Route verification complete!');
    } catch (err) {
      setVerifyErr(err.message);
      toast.error(`Route probe failed: ${err.message}`);
    } finally {
      setVerifyLoading(false);
    }
  }

  // ── Test Lab Execution ──
  async function runTest() {
    setTestLoading(true);
    setTestError('');
    setTestResult(null);
    try {
      let body = { mode: testMode(), task: 'fissal' };
      if (testMode() === 'chat' || testMode() === 'discord-preview') {
        body.prompt = testPrompt();
      } else if (testMode() === 'rag-probe') {
        body.prompt = testPrompt();
        body.probe_kind = testRagProbeKind();
      } else if (testMode() === 'suggest-price') {
        body.itemName = testItemName();
      } else if (testMode() === 'bulk-pricing') {
        body.items = testBulkItems();
      }

      const res = await fetch('/api/admin/fissal/test', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTestResult(data);
      toast.success('Test run completed!');
    } catch (err) {
      setTestError(err.message);
      toast.error(`Test failed: ${err.message}`);
    } finally {
      setTestLoading(false);
    }
  }

  // ── Clear AI Logs ──
  async function clearAiLogs() {
    if (!confirm('Are you sure you want to delete all AI invocation history logs?')) return;
    setDeleteLogsLoading(true);
    try {
      const res = await fetch('/api/admin/fissal/invocations', {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      toast.success(`Deleted ${data.deleted || 0} invocation records`);
      refetch();
    } catch (err) {
      toast.error(`Failed to delete logs: ${err.message}`);
    } finally {
      setDeleteLogsLoading(false);
    }
  }

  // ── Sorted Invocations for Telemetry ──
  const sortedInvocations = createMemo(() => {
    const rows = rawInvocations();
    const k = sortKey();
    const dir = sortDir();
    const isText = k === 'source' || k === 'model' || k === 'endpoint';
    return [...rows].sort((a, b) => {
      const va = a[k];
      const vb = b[k];
      if (isText) return String(va || '').localeCompare(String(vb || '')) * dir;
      return ((Number(va) || 0) - (Number(vb) || 0)) * dir;
    });
  });

  function toggleSort(k) {
    if (sortKey() === k) setSortDir(d => -d);
    else { setSortKey(k); setSortDir(-1); }
  }

  function sortIndicator(k) {
    if (sortKey() !== k) return '';
    return sortDir() === 1 ? ' ▲' : ' ▼';
  }

  // Max daily cost helper for bar chart
  const maxDailyCost = () => Math.max(...daily().map(d => d.cost), 0.000001);

  // Load config on mount
  createEffect(() => {
    if (isAdmin()) {
      loadModelConfig();
    }
  });

  return (
    <div class="v2-widget">
      {/* ── Top Navigation Bar ── */}
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__sub" style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
          <span>🐾</span>
          <span>Fissal Control</span>
        </span>

        <Show when={isAdmin()}>
          <button
            class={`v2-gt-chip ${tab() === 'bastion' ? 'is-on' : ''}`}
            style={{ 'margin-left': 'auto', 'font-size': '11px', 'font-weight': 600 }}
            onClick={() => setTab('bastion')}
          >
            ⚡ Bastion Engine
          </button>
          <button
            class={`v2-gt-chip ${tab() === 'metrics' ? 'is-on' : ''}`}
            style={{ 'font-size': '11px' }}
            onClick={() => setTab('metrics')}
          >
            📊 Telemetry & Costs
          </button>
          <button
            class={`v2-gt-chip ${tab() === 'memory' ? 'is-on' : ''}`}
            style={{ 'font-size': '11px' }}
            onClick={() => setTab('memory')}
          >
            🧠 Memory & Lore
          </button>
          <button
            class={`v2-gt-chip ${tab() === 'tools' ? 'is-on' : ''}`}
            style={{ 'font-size': '11px' }}
            onClick={() => {
              setTab('tools');
              if (toolsList().length === 0) loadToolsList();
            }}
          >
            🛠️ Tools & Test Lab
          </button>
        </Show>

        <button
          class="v2-widget-hdr__btn"
          style={{ 'margin-left': isAdmin() ? '6px' : 'auto' }}
          title="Refresh All Telemetry"
          onClick={() => { refetch(); if (isAdmin()) loadModelConfig(); }}
        >
          ↻
        </button>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          TAB 1: BASTION ENGINE (Echo & Fissal Co-Pilot)
          ════════════════════════════════════════════════════════════════════════ */}
      <Show when={tab() === 'bastion' && isAdmin()}>
        <div class="v2-widget__scroll" style={{ padding: '14px', display: 'flex', 'flex-direction': 'column', gap: '16px' }}>
          {/* Echo & Fissal Co-Pilot Banner */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(16, 185, 129, 0.08) 50%, rgba(13, 13, 18, 0.8) 100%)',
            border: '1px solid rgba(245, 158, 11, 0.35)',
            'border-radius': '12px',
            padding: '16px',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'flex-start', 'flex-wrap': 'wrap', gap: '10px' }}>
              <div>
                <div style={{ display: 'flex', 'align-items': 'center', gap: '10px' }}>
                  <span style={{ 'font-size': '22px' }}>🐈‍⬛⚙️</span>
                  <div>
                    <div style={{ 'font-size': '16px', 'font-weight': 800, color: 'var(--gold, #ffcc55)', 'letter-spacing': '0.02em' }}>
                      Echo & Fissal: Bastion Command Engine
                    </div>
                    <div class="v2-dim" style={{ 'font-size': '11px', 'margin-top': '2px' }}>
                      Unified Homelab Intelligence, Persona Anchoring, & Operational Co-Pilot
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Indicator Badge */}
              <div style={{
                display: 'flex',
                'align-items': 'center',
                gap: '8px',
                padding: '6px 12px',
                'border-radius': '20px',
                background: msEnabled() ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                border: msEnabled() ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid rgba(245, 158, 11, 0.4)',
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  'border-radius': '50%',
                  background: msEnabled() ? '#10b981' : 'var(--gold, #f59e0b)',
                  'box-shadow': msEnabled() ? '0 0 10px #10b981' : '0 0 10px #f59e0b',
                }}></span>
                <span style={{
                  'font-size': '11px',
                  'font-weight': 700,
                  color: msEnabled() ? '#34d399' : 'var(--gold, #fbbf24)',
                  'font-family': 'var(--font-mono, monospace)',
                }}>
                  {msEnabled() ? 'UNIFIED BASTION ARMED' : 'TASK ROUTING PASSIVE'}
                </span>
              </div>
            </div>

            {/* Quick Summary Strip */}
            <div style={{
              display: 'flex',
              gap: '16px',
              'margin-top': '14px',
              'padding-top': '12px',
              'border-top': '1px solid rgba(255, 255, 255, 0.08)',
              'flex-wrap': 'wrap',
              'font-size': '11px',
            }}>
              <div>
                <span class="v2-dim">Primary Brain: </span>
                <span style={{ color: 'var(--gold, #ffcc55)', 'font-weight': 600 }}>{maskModelName(msModel())}</span>
              </div>
              <div>
                <span class="v2-dim">Thinking Dial: </span>
                <span style={{ color: '#60a5fa', 'font-weight': 600 }}>{msReasoning()}</span>
              </div>
              <div>
                <span class="v2-dim">Host Topology: </span>
                <span style={{ color: 'var(--t-green, #4ade80)' }}>echodedicated (192.168.68.50)</span>
              </div>
              <div>
                <span class="v2-dim">Surfaces: </span>
                <span style={{ color: '#e0e7ff' }}>Discord · Web Terminal · OpenWebUI · Prime</span>
              </div>
            </div>
          </div>

          {/* ── Grid: Main Engine Tuning & Live Route Probe ── */}
          <div style={{ display: 'grid', 'grid-template-columns': 'repeat(auto-fit, minmax(320px, 1fr))', gap: '14px' }}>
            {/* Card 1: Primary Intelligence & Thinking Dial */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              'border-radius': '10px',
              padding: '14px',
              display: 'flex',
              'flex-direction': 'column',
              gap: '12px',
            }}>
              <div style={{ 'font-size': '13px', 'font-weight': 700, color: 'var(--gold, #ffcc55)', display: 'flex', 'align-items': 'center', gap: '6px' }}>
                <span>🧠</span>
                <span>Bastion Primary Intelligence</span>
              </div>
              <div class="v2-dim" style={{ 'font-size': '11px', 'line-height': '1.4' }}>
                Select Fissal's foundational neural brain and reasoning budget. Changes propagate across Discord, Web, and connected homelab surfaces.
              </div>

              {/* Model Dropdown & Presets */}
              <div>
                <label class="v2-label" style={{ 'font-size': '11px', display: 'block', 'margin-bottom': '4px' }}>
                  Core Model Selection
                </label>
                <select
                  class="v2-input"
                  value={msModel()}
                  onChange={(e) => setMsModel(e.currentTarget.value)}
                  style={{ width: '100%', 'font-family': 'var(--font-mono, monospace)', 'font-size': '12px' }}
                >
                  <option value="gemini-4-argon">gemini-4-argon (Recommended — Frontier Argon Reasoning & Multimodal)</option>
                  <option value="gemini-3.8-flash">gemini-3.8-flash (Fast Micro-Tasks & Sub-Second Reflex)</option>
                  <option value="gemini-3.7-flash">gemini-3.7-flash (Alternative Flash)</option>
                  <option value="gemini-2.5-pro">gemini-2.5-pro (Deep Synthesis & Pro Reasoning)</option>
                  <For each={availModels().filter(m => !['gemini-4-argon', 'gemini-3.8-flash', 'gemini-3.7-flash', 'gemini-2.5-pro'].includes(m))}>{(m) => (
                    <option value={m}>{maskModelName(m)}</option>
                  )}</For>
                </select>

                {/* Quick Model Presets */}
                <div style={{ display: 'flex', gap: '6px', 'margin-top': '6px', 'flex-wrap': 'wrap' }}>
                  <button
                    class={`v2-perf-filter-chip ${msModel() === 'gemini-4-argon' ? 'is-on' : ''}`}
                    onClick={() => setMsModel('gemini-4-argon')}
                  >
                    💎 Gemini 4 Argon
                  </button>
                  <button
                    class={`v2-perf-filter-chip ${msModel() === 'gemini-3.8-flash' ? 'is-on' : ''}`}
                    onClick={() => setMsModel('gemini-3.8-flash')}
                  >
                    ⚡ Gemini 3.8 Flash
                  </button>
                  <button
                    class={`v2-perf-filter-chip ${msModel() === 'gemini-2.5-pro' ? 'is-on' : ''}`}
                    onClick={() => setMsModel('gemini-2.5-pro')}
                  >
                    🔬 Gemini 2.5 Pro
                  </button>
                  <button
                    class={`v2-perf-filter-chip ${msModel() === 'gemini-3.7-flash' ? 'is-on' : ''}`}
                    onClick={() => setMsModel('gemini-3.7-flash')}
                  >
                    ✨ Gemini 3.7 Flash
                  </button>
                </div>
              </div>

              {/* Thinking Dial */}
              <div style={{ 'margin-top': '4px' }}>
                <label class="v2-label" style={{ 'font-size': '11px', display: 'block', 'margin-bottom': '6px' }}>
                  Dynamic Thinking Dial: <span style={{ color: '#60a5fa' }}>{msReasoning()}</span>
                </label>
                <div style={{ display: 'grid', 'grid-template-columns': 'repeat(3, 1fr)', gap: '6px' }}>
                  <button
                    type="button"
                    class={`v2-gt-chip ${msReasoning() === 'LOW' || msReasoning() === 'MINIMAL' ? 'is-on' : ''}`}
                    style={{ 'font-size': '11px', padding: '6px 4px', 'text-align': 'center' }}
                    onClick={() => setMsReasoning('LOW')}
                    title="Snappy reflex responses with minimal thinking token overhead"
                  >
                    ⚡ Reflex (Low)
                  </button>
                  <button
                    type="button"
                    class={`v2-gt-chip ${msReasoning() === 'AUTO' ? 'is-on' : ''}`}
                    style={{ 'font-size': '11px', padding: '6px 4px', 'text-align': 'center' }}
                    onClick={() => setMsReasoning('AUTO')}
                    title="Context-adaptive thinking budget dynamically adjusted per task"
                  >
                    🎯 Adaptive (Auto)
                  </button>
                  <button
                    type="button"
                    class={`v2-gt-chip ${msReasoning() === 'HIGH' ? 'is-on' : ''}`}
                    style={{ 'font-size': '11px', padding: '6px 4px', 'text-align': 'center' }}
                    onClick={() => setMsReasoning('HIGH')}
                    title="Deep deliberate reasoning for complex architecture and debugging"
                  >
                    🔬 Deep (High)
                  </button>
                </div>
              </div>

              {/* Surface Sync & Master Enforce Checkboxes */}
              <div style={{ display: 'flex', 'flex-direction': 'column', gap: '8px', 'margin-top': '4px' }}>
                <label style={{ display: 'flex', 'align-items': 'center', gap: '8px', 'font-size': '11px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={msEnabled()}
                    onChange={(e) => setMsEnabled(e.currentTarget.checked)}
                  />
                  <span><strong>Enforce Master Brain:</strong> Override all 9 subtasks with this model</span>
                </label>
                <label style={{ display: 'flex', 'align-items': 'center', gap: '8px', 'font-size': '11px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={msSyncOwu()}
                    onChange={(e) => setMsSyncOwu(e.currentTarget.checked)}
                  />
                  <span><strong>OpenWebUI Sync:</strong> Update <code style={{ color: 'var(--fissal)' }}>fissal</code> & <code style={{ color: 'var(--fissal)' }}>fissal-prime</code> base models</span>
                </label>
              </div>

              {/* Action Button */}
              <div style={{ 'margin-top': '6px' }}>
                <button
                  class="v2-btn-sm"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    background: 'linear-gradient(90deg, #f59e0b 0%, #10b981 100%)',
                    color: '#000',
                    'font-weight': 800,
                    'font-size': '12px',
                    cursor: msSaving() ? 'wait' : 'pointer',
                    opacity: msSaving() ? 0.7 : 1,
                  }}
                  disabled={msSaving()}
                  onClick={saveUnifiedEngine}
                >
                  {msSaving() ? '⚙️ Synchronizing Bastion Engine…' : msSaved() ? '✓ Bastion Engine Synchronized!' : '⚡ Apply & Synchronize Bastion Engine'}
                </button>
                <Show when={msErr()}>
                  <div class="v2-error" style={{ 'margin-top': '6px', 'font-size': '11px' }}>{msErr()}</div>
                </Show>
              </div>
            </div>

            {/* Card 2: Live Route Verification Probe */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              'border-radius': '10px',
              padding: '14px',
              display: 'flex',
              'flex-direction': 'column',
              gap: '12px',
            }}>
              <div style={{ 'font-size': '13px', 'font-weight': 700, color: 'var(--cyan, #38bdf8)', display: 'flex', 'align-items': 'center', gap: '6px' }}>
                <span>🎯</span>
                <span>Live Route & Persona Probe</span>
              </div>
              <div class="v2-dim" style={{ 'font-size': '11px', 'line-height': '1.4' }}>
                Send a real-time probe to test upstream model latency, thinking tokens, and Fissal Khajiit persona adherence.
              </div>

              <div>
                <label class="v2-label" style={{ 'font-size': '11px', display: 'block', 'margin-bottom': '4px' }}>Probe Query</label>
                <input
                  class="v2-input"
                  type="text"
                  value={verifyPrompt()}
                  onInput={(e) => setVerifyPrompt(e.currentTarget.value)}
                  style={{ width: '100%', 'font-size': '11px' }}
                />
              </div>

              <div>
                <button
                  class="v2-btn-sm"
                  style={{ width: '100%', padding: '7px 12px', 'font-size': '11px', 'font-weight': 600 }}
                  disabled={verifyLoading()}
                  onClick={runVerifyRoute}
                >
                  {verifyLoading() ? '📡 Probing Upstream Route…' : '📡 Probe Co-Pilot Route Now'}
                </button>
              </div>

              {/* Probe Result Box */}
              <Show when={verifyLoading()}>
                <div class="v2-widget-skel" style={{ padding: '12px', 'text-align': 'center' }}>
                  Awaiting generation & thinking tokens…
                </div>
              </Show>

              <Show when={verifyErr()}>
                <div class="v2-error" style={{ 'font-size': '11px' }}>{verifyErr()}</div>
              </Show>

              <Show when={verifyResult() && !verifyLoading()}>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.35)',
                  border: '1px solid rgba(56, 189, 248, 0.25)',
                  'border-radius': '8px',
                  padding: '10px',
                  display: 'flex',
                  'flex-direction': 'column',
                  gap: '8px',
                }}>
                  <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: '6px' }}>
                    <div style={{ display: 'flex', gap: '6px', 'align-items': 'center' }}>
                      <span style={{
                        padding: '2px 6px',
                        'border-radius': '4px',
                        background: 'rgba(16, 185, 129, 0.2)',
                        color: '#34d399',
                        'font-weight': 700,
                        'font-size': '10px',
                        'font-family': 'var(--font-mono, monospace)',
                      }}>
                        {verifyResult()?.status?.toUpperCase()}
                      </span>
                      <span style={{ 'font-size': '11px', 'font-weight': 600, color: 'var(--gold, #ffcc55)' }}>
                        {maskModelName(verifyResult()?.resolved_model)}
                      </span>
                    </div>
                    <span class="v2-dim" style={{ 'font-size': '10px', 'font-family': 'var(--font-mono, monospace)' }}>
                      ⏱️ {verifyResult()?.duration_ms}ms · Provider: {verifyResult()?.upstream_provider}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', 'font-size': '10px', 'font-family': 'var(--font-mono, monospace)' }}>
                    <span style={{ color: '#60a5fa' }}>Thinking: {verifyResult()?.thoughts_token_count || 0} tok</span>
                    <span style={{ color: '#34d399' }}>Output: {verifyResult()?.candidates_token_count || 0} tok</span>
                    <span class="v2-dim">Prompt: {verifyResult()?.prompt_token_count || 0} tok</span>
                  </div>

                  <div style={{
                    'max-height': '120px',
                    'overflow-y': 'auto',
                    background: 'rgba(0, 0, 0, 0.25)',
                    padding: '8px',
                    'border-radius': '6px',
                    'font-size': '11px',
                    'line-height': '1.4',
                  }}>
                    {renderFissalMarkdown(verifyResult()?.output_text || '')}
                  </div>
                </div>
              </Show>
            </div>
          </div>

          {/* ── Auxiliary Services & Grounding Vitals ── */}
          <div>
            <div class="v2-section-h" style={{ 'margin-bottom': '8px', display: 'flex', 'align-items': 'center', gap: '6px' }}>
              <span>🛰️</span>
              <span>Auxiliary Intelligence & Grounding Vitals</span>
            </div>
            <div style={{ display: 'grid', 'grid-template-columns': 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
              {/* Vitals Card 1: Fido Retriever Hound */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                'border-radius': '8px',
                padding: '10px 12px',
              }}>
                <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '4px' }}>
                  <span style={{ 'font-weight': 700, 'font-size': '12px', color: 'var(--gold, #f59e0b)' }}>🐕 Fissal's Fido (Hound)</span>
                  <span style={{ 'font-size': '9px', padding: '1px 5px', 'border-radius': '4px', background: 'rgba(16, 185, 129, 0.2)', color: '#34d399' }}>Active</span>
                </div>
                <div class="v2-dim" style={{ 'font-size': '10px' }}>
                  Interactions API auxiliary retriever for deferred tool calling across Discord, OpenWebUI & Antigravity.
                </div>
              </div>

              {/* Vitals Card 2: Qdrant Vector DB */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                'border-radius': '8px',
                padding: '10px 12px',
              }}>
                <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '4px' }}>
                  <span style={{ 'font-weight': 700, 'font-size': '12px', color: 'var(--cyan, #38bdf8)' }}>💎 Qdrant Vector DB</span>
                  <span style={{ 'font-size': '9px', padding: '1px 5px', 'border-radius': '4px', background: 'rgba(16, 185, 129, 0.2)', color: '#34d399' }}>Port 6333</span>
                </div>
                <div class="v2-dim" style={{ 'font-size': '10px' }}>
                  Codebase semantic search embeddings, memories, and high-dimensional recall index.
                </div>
              </div>

              {/* Vitals Card 3: SQLite Neural Anchors */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                'border-radius': '8px',
                padding: '10px 12px',
              }}>
                <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '4px' }}>
                  <span style={{ 'font-weight': 700, 'font-size': '12px', color: '#a78bfa' }}>📜 SQLite Neural Lore</span>
                  <span style={{ 'font-size': '9px', padding: '1px 5px', 'border-radius': '4px', background: 'rgba(16, 185, 129, 0.2)', color: '#34d399' }}>aimemory.db</span>
                </div>
                <div class="v2-dim" style={{ 'font-size': '10px' }}>
                  User facts, server preferences, guild history, and Khajiit personality anchors.
                </div>
              </div>

              {/* Vitals Card 4: Homelab Ecosystem */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                'border-radius': '8px',
                padding: '10px 12px',
              }}>
                <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '4px' }}>
                  <span style={{ 'font-weight': 700, 'font-size': '12px', color: 'var(--t-green, #4ade80)' }}>🏠 Homelab Ecosystem</span>
                  <span style={{ 'font-size': '9px', padding: '1px 5px', 'border-radius': '4px', background: 'rgba(56, 189, 248, 0.2)', color: 'var(--cyan, #38bdf8)' }}>192.168.68.50</span>
                </div>
                <div class="v2-dim" style={{ 'font-size': '10px' }}>
                  Frigate NVR (:5000), Immich (:2283), Tuya Smart Lights, Host Ollama (:11434).
                </div>
              </div>
            </div>
          </div>

          {/* ── Expandable Drawer: OpenWebUI Deep Tuning ── */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.015)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            'border-radius': '8px',
            padding: '10px 14px',
          }}>
            <button
              type="button"
              style={{
                display: 'flex',
                'justify-content': 'space-between',
                'align-items': 'center',
                width: '100%',
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                padding: '4px 0',
              }}
              onClick={() => {
                const next = !owuOpen();
                setOwuOpen(next);
                if (next && !owuModel()) loadOpenWebUiModel();
              }}
            >
              <div style={{ display: 'flex', 'align-items': 'center', gap: '8px', 'font-size': '12px', 'font-weight': 600 }}>
                <span>🦙</span>
                <span>OpenWebUI Integration Deep Parameters (:3001)</span>
              </div>
              <span style={{ 'font-size': '11px', color: 'var(--t-dim, #94a3b8)' }}>{owuOpen() ? '▲ Collapse' : '▼ Expand'}</span>
            </button>

            <Show when={owuOpen()}>
              <div style={{ 'margin-top': '12px', 'padding-top': '10px', 'border-top': '1px solid rgba(255, 255, 255, 0.06)' }}>
                <Show when={owuLoading()}>
                  <div class="v2-widget-skel" style={{ padding: '8px' }}>Loading OpenWebUI Model parameters…</div>
                </Show>

                <Show when={!owuLoading() && owuModel()}>
                  <div style={{ display: 'flex', 'flex-direction': 'column', gap: '10px' }}>
                    <div style={{ display: 'grid', 'grid-template-columns': 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                      <div>
                        <label class="v2-label" style={{ 'font-size': '10px', display: 'block', 'margin-bottom': '2px' }}>Base Model ID</label>
                        <input
                          class="v2-input"
                          type="text"
                          value={owuBaseModel()}
                          onInput={(e) => setOwuBaseModel(e.currentTarget.value)}
                          placeholder="e.g. google/gemini-4-argon"
                          style={{ 'font-size': '11px' }}
                        />
                      </div>
                      <div>
                        <label class="v2-label" style={{ 'font-size': '10px', display: 'block', 'margin-bottom': '2px' }}>Temperature ({owuTemp()})</label>
                        <input
                          type="range"
                          min="0"
                          max="1.5"
                          step="0.05"
                          value={owuTemp()}
                          onInput={(e) => setOwuTemp(parseFloat(e.currentTarget.value))}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label class="v2-label" style={{ 'font-size': '10px', display: 'block', 'margin-bottom': '2px' }}>Top P ({owuTopP()})</label>
                        <input
                          type="range"
                          min="0.1"
                          max="1.0"
                          step="0.05"
                          value={owuTopP()}
                          onInput={(e) => setOwuTopP(parseFloat(e.currentTarget.value))}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>

                    <div>
                      <label class="v2-label" style={{ 'font-size': '10px', display: 'block', 'margin-bottom': '2px' }}>OpenWebUI System Prompt</label>
                      <textarea
                        class="v2-input"
                        rows="4"
                        style={{ width: '100%', 'font-family': 'var(--font-mono, monospace)', 'font-size': '10px' }}
                        value={owuSystem()}
                        onInput={(e) => setOwuSystem(e.currentTarget.value)}
                      />
                    </div>

                    <div style={{ display: 'flex', 'justify-content': 'flex-end', gap: '8px' }}>
                      <button class="v2-btn-sm" onClick={loadOpenWebUiModel} disabled={owuSaving()}>↻ Reset</button>
                      <button
                        class="v2-btn-sm"
                        style={{ background: 'var(--gold, #ffcc55)', color: '#000', 'font-weight': 700 }}
                        onClick={saveOpenWebUiModel}
                        disabled={owuSaving()}
                      >
                        {owuSaving() ? 'Saving…' : 'Save to OpenWebUI'}
                      </button>
                    </div>
                  </div>
                </Show>
              </div>
            </Show>
          </div>
        </div>
      </Show>

      {/* ════════════════════════════════════════════════════════════════════════
          TAB 2: METRICS & COSTS (Telemetry & Spend)
          ════════════════════════════════════════════════════════════════════════ */}
      <Show when={tab() === 'metrics'}>
        <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading telemetry…</div>}>
          <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
            <div class="v2-widget__scroll">
              {/* Totals Stat Grid */}
              <div class="v2-stat-grid">
                <div class="v2-stat v2-stat--gold">
                  <span class="v2-stat__label">Total Cost</span>
                  <span class="v2-stat__value">{formatCost(totals().cost)}</span>
                </div>
                <div class="v2-stat v2-stat--cyan">
                  <span class="v2-stat__label">Invocations</span>
                  <span class="v2-stat__value">{fmtNum(totals().count)}</span>
                </div>
                <div class="v2-stat">
                  <span class="v2-stat__label">Prompt Tokens</span>
                  <span class="v2-stat__value">{fmtNum(totals().tokensIn)}</span>
                </div>
                <div class="v2-stat">
                  <span class="v2-stat__label">Output Tokens</span>
                  <span class="v2-stat__value">{fmtNum(totals().tokensOut)}</span>
                </div>
              </div>

              {/* By-Model Breakdown Cards */}
              <Show when={byModel().length > 0}>
                <div class="v2-section-h" style={{ 'margin-top': '12px' }}>Spend by Model</div>
                <div class="v2-fissal-cards">
                  <For each={byModel()}>{(m) => (
                    <div class="v2-fissal-card">
                      <div class="v2-fissal-card__name" title={m.model}>{maskModelName(m.model)}</div>
                      <div class="v2-fissal-card__stat">{formatCost(m.cost)}</div>
                      <div class="v2-fissal-card__sub">{fmtNum(m.count)} calls · {fmtNum(m.tokens ?? ((m.tokensIn || 0) + (m.tokensOut || 0)))} tok</div>
                    </div>
                  )}</For>
                </div>
              </Show>

              {/* Daily Cost Bar Chart */}
              <Show when={daily().length > 0}>
                <div class="v2-section-h" style={{ 'margin-top': '14px' }}>Daily Cost (Past 14 Days)</div>
                <div class="v2-fissal-chart">
                  <For each={daily()}>{(d) => {
                    const pct = () => Math.round((d.cost / maxDailyCost()) * 100);
                    return (
                      <div class="v2-fissal-bar" title={`${dayLabel(d.day)}: ${formatCost(d.cost)} (${fmtNum(d.count)} calls)`}>
                        <div class="v2-fissal-bar__fill" style={{ height: `${Math.max(pct(), 4)}%` }} />
                        <span class="v2-fissal-bar__label">{dayLabel(d.day)}</span>
                      </div>
                    );
                  }}</For>
                </div>
              </Show>

              {/* Recent Invocations Table */}
              <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-top': '16px', 'margin-bottom': '6px' }}>
                <div class="v2-section-h" style={{ margin: 0 }}>Recent Invocations ({sortedInvocations().length})</div>
                <Show when={isAdmin()}>
                  <button class="v2-btn-sm" style={{ 'font-size': '10px', color: 'var(--danger, #f87171)' }} onClick={clearAiLogs} disabled={deleteLogsLoading()}>
                    {deleteLogsLoading() ? 'Clearing…' : 'Clear Invocation Logs'}
                  </button>
                </Show>
              </div>

              <Show when={sortedInvocations().length > 0} fallback={<div class="v2-dim" style={{ 'font-size': '11px' }}>No invocation logs recorded yet.</div>}>
                <table class="v2-perf-table" style={{ width: '100%', 'font-size': '11px' }}>
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('timestamp')}>Time{sortIndicator('timestamp')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('source')}>Source{sortIndicator('source')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('model')}>Model{sortIndicator('model')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('cost')}>Cost{sortIndicator('cost')}</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('duration_ms')}>Latency{sortIndicator('duration_ms')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={sortedInvocations()}>{(inv) => (
                      <tr>
                        <td class="v2-dim" style={{ 'font-family': 'var(--font-mono, monospace)' }}>{relTime(inv.timestamp)}</td>
                        <td><span class="v2-fissal-chip">{inv.source}</span></td>
                        <td style={{ 'font-family': 'var(--font-mono, monospace)', color: 'var(--gold, #ffcc55)' }}>{maskModelName(inv.model)}</td>
                        <td style={{ 'font-family': 'var(--font-mono, monospace)' }}>{formatCost(inv.cost)}</td>
                        <td class="v2-dim" style={{ 'font-family': 'var(--font-mono, monospace)' }}>{inv.duration_ms ? `${inv.duration_ms}ms` : '—'}</td>
                      </tr>
                    )}</For>
                  </tbody>
                </table>
              </Show>
            </div>
          </Show>
        </Show>
      </Show>

      {/* ════════════════════════════════════════════════════════════════════════
          TAB 3: NEURAL MEMORY & LORE (User Facts & Grounding)
          ════════════════════════════════════════════════════════════════════════ */}
      <Show when={tab() === 'memory' && isAdmin()}>
        <div style={{ display: 'flex', 'flex-direction': 'column', height: '100%', overflow: 'hidden' }}>
          {/* Subtab Toggle Strip */}
          <div style={{
            display: 'flex',
            gap: '8px',
            padding: '8px 12px',
            background: 'rgba(255, 255, 255, 0.02)',
            'border-bottom': '1px solid rgba(255, 255, 255, 0.06)',
          }}>
            <button
              class={`v2-gt-chip ${memorySubTab() === 'users' ? 'is-on' : ''}`}
              style={{ 'font-size': '11px' }}
              onClick={() => setMemorySubTab('users')}
            >
              👤 User Lore & Facts (aimemory.db)
            </button>
            <button
              class={`v2-gt-chip ${memorySubTab() === 'grounding' ? 'is-on' : ''}`}
              style={{ 'font-size': '11px' }}
              onClick={() => setMemorySubTab('grounding')}
            >
              📚 Grounding Knowledge Contexts
            </button>
          </div>

          <div style={{ flex: 1, overflow: 'auto' }}>
            <Show when={memorySubTab() === 'users'}>
              <AdminMemoriesPanel embedded />
            </Show>
            <Show when={memorySubTab() === 'grounding'}>
              <GroundingStudio />
            </Show>
          </div>
        </div>
      </Show>

      {/* ════════════════════════════════════════════════════════════════════════
          TAB 4: LIVE TOOLS & TEST LAB (Capabilities & Sandbox)
          ════════════════════════════════════════════════════════════════════════ */}
      <Show when={tab() === 'tools' && isAdmin()}>
        <div style={{ display: 'flex', 'flex-direction': 'column', height: '100%', overflow: 'hidden' }}>
          {/* Subtab Toggle Strip */}
          <div style={{
            display: 'flex',
            gap: '8px',
            padding: '8px 12px',
            background: 'rgba(255, 255, 255, 0.02)',
            'border-bottom': '1px solid rgba(255, 255, 255, 0.06)',
          }}>
            <button
              class={`v2-gt-chip ${toolsSubTab() === 'sandbox' ? 'is-on' : ''}`}
              style={{ 'font-size': '11px' }}
              onClick={() => setToolsSubTab('sandbox')}
            >
              🧪 Response Sandbox & Testing
            </button>
            <button
              class={`v2-gt-chip ${toolsSubTab() === 'registry' ? 'is-on' : ''}`}
              style={{ 'font-size': '11px' }}
              onClick={() => {
                setToolsSubTab('registry');
                if (toolsList().length === 0) loadToolsList();
              }}
            >
              🛠️ Tools Registry & Direct Execution
            </button>
          </div>

          <div class="v2-widget__scroll" style={{ flex: 1, padding: '12px' }}>
            {/* SUBTAB A: RESPONSE SANDBOX */}
            <Show when={toolsSubTab() === 'sandbox'}>
              <div style={{ display: 'flex', 'flex-direction': 'column', gap: '12px' }}>
                <div class="v2-section-h" style={{ margin: 0 }}>Interactive Response Sandbox</div>
                <div class="v2-dim" style={{ 'font-size': '11px' }}>
                  Execute one-off test queries across Discord preview, RAG retrieval probes, or direct ESO pricing calculations.
                </div>

                {/* Mode Selector Chips */}
                <div style={{ display: 'flex', gap: '6px', 'flex-wrap': 'wrap' }}>
                  <button
                    class={`v2-gt-chip ${testMode() === 'chat' ? 'is-on' : ''}`}
                    onClick={() => setTestMode('chat')}
                  >
                    💬 Prompt Test
                  </button>
                  <button
                    class={`v2-gt-chip ${testMode() === 'discord-preview' ? 'is-on' : ''}`}
                    onClick={() => setTestMode('discord-preview')}
                  >
                    🎮 Discord Session Preview
                  </button>
                  <button
                    class={`v2-gt-chip ${testMode() === 'rag-probe' ? 'is-on' : ''}`}
                    onClick={() => setTestMode('rag-probe')}
                  >
                    🔍 RAG Retrieval Probe
                  </button>
                  <button
                    class={`v2-gt-chip ${testMode() === 'suggest-price' ? 'is-on' : ''}`}
                    onClick={() => setTestMode('suggest-price')}
                  >
                    💰 Single Price Suggestion
                  </button>
                  <button
                    class={`v2-gt-chip ${testMode() === 'bulk-pricing' ? 'is-on' : ''}`}
                    onClick={() => setTestMode('bulk-pricing')}
                  >
                    📦 Bulk Pricing Analysis
                  </button>
                </div>

                {/* Inputs by mode */}
                <Show when={testMode() === 'chat' || testMode() === 'discord-preview' || testMode() === 'rag-probe'}>
                  <div>
                    <label class="v2-label" style={{ 'font-size': '11px', display: 'block', 'margin-bottom': '4px' }}>
                      {testMode() === 'discord-preview' ? 'Discord Message Prompt' : testMode() === 'rag-probe' ? 'Knowledge Query' : 'Prompt Content'}
                    </label>
                    <textarea
                      class="v2-input"
                      rows="3"
                      placeholder={testMode() === 'discord-preview' ? 'e.g. Fissal, what are the current market trends for tempering alloys?' : 'Enter prompt...'}
                      value={testPrompt()}
                      onInput={(e) => setTestPrompt(e.currentTarget.value)}
                      style={{ width: '100%', 'font-family': 'var(--font-mono, monospace)', 'font-size': '11px' }}
                    />
                  </div>
                </Show>

                <Show when={testMode() === 'suggest-price'}>
                  <div>
                    <label class="v2-label" style={{ 'font-size': '11px', display: 'block', 'margin-bottom': '4px' }}>ESO Item Name</label>
                    <input
                      class="v2-input"
                      type="text"
                      value={testItemName()}
                      onInput={(e) => setTestItemName(e.currentTarget.value)}
                      placeholder="e.g. Dreugh Wax"
                      style={{ width: '100%', 'font-size': '11px' }}
                    />
                  </div>
                </Show>

                <Show when={testMode() === 'bulk-pricing'}>
                  <div>
                    <label class="v2-label" style={{ 'font-size': '11px', display: 'block', 'margin-bottom': '4px' }}>Item List (one per line)</label>
                    <textarea
                      class="v2-input"
                      rows="4"
                      value={testBulkItems()}
                      onInput={(e) => setTestBulkItems(e.currentTarget.value)}
                      style={{ width: '100%', 'font-family': 'var(--font-mono, monospace)', 'font-size': '11px' }}
                    />
                  </div>
                </Show>

                {/* Run Test Button */}
                <div>
                  <button
                    class="v2-btn-sm"
                    style={{ background: 'var(--gold, #ffcc55)', color: '#000', 'font-weight': 700, padding: '7px 14px' }}
                    disabled={testLoading()}
                    onClick={runTest}
                  >
                    {testLoading() ? 'Running Test…' : '▶ Run Sandbox Test'}
                  </button>
                </div>

                <Show when={testError()}>
                  <div class="v2-error" style={{ 'font-size': '11px' }}>{testError()}</div>
                </Show>

                {/* Test Result Inspector */}
                <Show when={testResult()}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    'border-radius': '8px',
                    padding: '12px',
                  }}>
                    <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '8px' }}>
                      <span style={{ 'font-weight': 700, 'font-size': '12px', color: 'var(--gold, #ffcc55)' }}>
                        Response Output
                      </span>
                      <span class="v2-dim" style={{ 'font-size': '10px', 'font-family': 'var(--font-mono, monospace)' }}>
                        {testResult()?.usage?.duration_ms ? `${testResult().usage.duration_ms}ms` : ''} ·
                        {testResult()?.usage?.tokens_out ? ` ${testResult().usage.tokens_out} tokens` : ''}
                      </span>
                    </div>

                    <div style={{ 'max-height': '240px', 'overflow-y': 'auto', 'font-size': '12px', 'line-height': '1.5' }}>
                      <Show when={typeof testResult()?.response === 'string'}>
                        {renderFissalMarkdown(testResult()?.response || '')}
                      </Show>
                      <Show when={testResult()?.result}>
                        <pre style={{ margin: 0, 'font-size': '11px', 'font-family': 'var(--font-mono, monospace)' }}>
                          {JSON.stringify(testResult()?.result, null, 2)}
                        </pre>
                      </Show>
                    </div>
                  </div>
                </Show>
              </div>
            </Show>

            {/* SUBTAB B: TOOL REGISTRY & DIRECT EXECUTION */}
            <Show when={toolsSubTab() === 'registry'}>
              <div style={{ display: 'flex', 'flex-direction': 'column', gap: '14px' }}>
                {/* Registry Header */}
                <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: '10px' }}>
                  <div>
                    <div class="v2-section-h" style={{ margin: 0 }}>Tool & Function-Calling Registry</div>
                    <div class="v2-dim" style={{ 'font-size': '11px' }}>
                      Inspect native function definitions, toggle availability, and execute direct test calls.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', 'align-items': 'center' }}>
                    <span style={{
                      padding: '3px 8px',
                      'border-radius': '12px',
                      'font-size': '11px',
                      'font-weight': 600,
                      background: 'rgba(74, 222, 128, 0.15)',
                      color: 'var(--t-green, #4ade80)',
                    }}>
                      ● {toolsList().filter(t => t.enabled).length} / {toolsList().length} Enabled
                    </span>
                    <button class="v2-btn-sm" disabled={toolsLoading()} onClick={loadToolsList}>
                      {toolsLoading() ? 'Refreshing…' : '↻ Refresh'}
                    </button>
                  </div>
                </div>

                {/* AI Tool Selection Probe */}
                <div style={{
                  border: '1px solid rgba(96, 165, 250, 0.3)',
                  background: 'rgba(96, 165, 250, 0.04)',
                  'border-radius': '8px',
                  padding: '12px',
                }}>
                  <div style={{ 'font-weight': 700, 'font-size': '12px', color: '#93c5fd', 'margin-bottom': '4px' }}>
                    💎 AI Tool Selection Probe (Gemini 4 Argon High Thinking)
                  </div>
                  <div class="v2-dim" style={{ 'font-size': '11px', 'margin-bottom': '8px' }}>
                    Test whether Gemini evaluates the user intent and correctly triggers tool calls with valid schemas.
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      class="v2-input"
                      type="text"
                      value={toolProbePrompt()}
                      onInput={(e) => setToolProbePrompt(e.currentTarget.value)}
                      style={{ flex: 1, 'font-size': '11px' }}
                    />
                    <button class="v2-btn-sm" disabled={toolProbeLoading()} onClick={runAiToolProbe}>
                      {toolProbeLoading() ? 'Testing…' : 'Probe Selection'}
                    </button>
                  </div>

                  <Show when={toolProbeResult()}>
                    <div style={{ 'margin-top': '8px', padding: '8px', background: 'rgba(0, 0, 0, 0.3)', 'border-radius': '6px', 'font-size': '11px' }}>
                      <div style={{ 'font-weight': 600, color: '#34d399' }}>
                        {toolProbeResult()?.tool_called ? `✓ Called: ${toolProbeResult()?.tool_name}` : 'No tool call needed'}
                      </div>
                      <Show when={toolProbeResult()?.tool_args}>
                        <div style={{ 'font-family': 'var(--font-mono, monospace)', 'font-size': '10px', color: '#e0e7ff', 'margin-top': '4px' }}>
                          Args: {JSON.stringify(toolProbeResult()?.tool_args)}
                        </div>
                      </Show>
                    </div>
                  </Show>
                </div>

                {/* Tools Table */}
                <table class="v2-perf-table" style={{ width: '100%', 'font-size': '11px' }}>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Tool Name</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={toolsList()}>{(tool) => (
                      <tr>
                        <td>
                          <button
                            class={`v2-perf-filter-chip ${tool.enabled ? 'is-on' : ''}`}
                            style={{ 'font-size': '10px', padding: '1px 6px' }}
                            onClick={() => toggleToolState(tool.name, tool.enabled)}
                          >
                            {tool.enabled ? 'ON' : 'OFF'}
                          </button>
                        </td>
                        <td style={{ 'font-family': 'var(--font-mono, monospace)', 'font-weight': 600, color: 'var(--gold, #ffcc55)' }}>
                          {tool.name}
                        </td>
                        <td class="v2-dim" style={{ 'font-size': '10px' }}>{tool.description}</td>
                        <td>
                          <button
                            class="v2-btn-sm"
                            style={{ 'font-size': '10px' }}
                            onClick={() => {
                              setSelectedToolForExec(tool);
                              setToolExecArgs('{}');
                              setToolExecResult(null);
                            }}
                          >
                            Execute
                          </button>
                        </td>
                      </tr>
                    )}</For>
                  </tbody>
                </table>

                {/* Direct Tool Execution Modal/Drawer */}
                <Show when={selectedToolForExec()}>
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 200, 50, 0.3)',
                    'border-radius': '8px',
                    padding: '12px',
                    'margin-top': '10px',
                  }}>
                    <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '8px' }}>
                      <span style={{ 'font-weight': 700, color: 'var(--gold, #ffcc55)' }}>
                        Direct Execution: {selectedToolForExec()?.name}
                      </span>
                      <button class="v2-btn-sm" onClick={() => setSelectedToolForExec(null)}>✕ Close</button>
                    </div>

                    <label class="v2-label" style={{ 'font-size': '10px', display: 'block', 'margin-bottom': '4px' }}>JSON Arguments</label>
                    <textarea
                      class="v2-input"
                      rows="2"
                      value={toolExecArgs()}
                      onInput={(e) => setToolExecArgs(e.currentTarget.value)}
                      style={{ width: '100%', 'font-family': 'var(--font-mono, monospace)', 'font-size': '11px', 'margin-bottom': '8px' }}
                    />

                    <button class="v2-btn-sm" disabled={toolExecLoading()} onClick={runDirectToolExec}>
                      {toolExecLoading() ? 'Executing…' : '▶ Execute Tool Now'}
                    </button>

                    <Show when={toolExecResult()}>
                      <pre style={{ 'margin-top': '8px', padding: '8px', background: 'rgba(0, 0, 0, 0.3)', 'border-radius': '6px', 'font-size': '10px', 'max-height': '150px', 'overflow-y': 'auto' }}>
                        {JSON.stringify(toolExecResult(), null, 2)}
                      </pre>
                    </Show>
                  </div>
                </Show>
              </div>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  );
}
